// Symbol for accessing the ledger log
export const LEDGER_SYMBOL = Symbol('ledger');

// Types for the ledger system
export interface LedgerEntry {
  timestamp: number;
  operation: 'read' | 'write';
  property: string | symbol;
  value?: any;
  previousValue?: any;
  stackTrace: string;
  threadId?: string;
}

export interface LedgerLog {
  initial: Record<string | symbol, any>;
  entries: LedgerEntry[];
  collisions: Array<{
    timestamp: number;
    property: string | symbol;
    operations: LedgerEntry[];
  }>;
  reconciliation: {
    pending: LedgerEntry[];
    reconciled: LedgerEntry[];
    conflicts: Array<{
      property: string | symbol;
      operations: LedgerEntry[];
      conflictType: 'overwrite' | 'nested' | 'race';
    }>;
  };
  metadata: {
    created: number;
    lastAccess: number;
    totalReads: number;
    totalWrites: number;
  };
}

// Thread ID generator for collision detection
let threadCounter = 0;
const threadId = `thread-${++threadCounter}-${Date.now()}`;

// Stack trace utility
function getStackTrace(): string {
  const stack = new Error().stack || '';
  // Remove the first few lines (Error constructor, getStackTrace, proxy handlers)
  return stack.split('\n').slice(3).join('\n');
}

// Safe value serialization utility
function safeStringify(value: any): string {
  try {
    return JSON.stringify(value);
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('circular')) {
      return '[Circular Reference]';
    } else {
      return `[Non-serializable: ${String(value)}]`;
    }
  }
}

// Collision detection window (ms)
const COLLISION_WINDOW = 10;

export function createLedger<T extends Record<string | symbol, any>>(
  initial: T,
  options: {
    collisionWindow?: number;
    enableStackTrace?: boolean;
  } = {}
): T & { [LEDGER_SYMBOL]: LedgerLog } {
  const collisionWindow = options.collisionWindow ?? COLLISION_WINDOW;
  const enableStackTrace = options.enableStackTrace ?? true;
  
  // Initialize the ledger
  const ledger: LedgerLog = {
    initial: { ...initial },
    entries: [],
    collisions: [],
    reconciliation: {
      pending: [],
      reconciled: [],
      conflicts: []
    },
    metadata: {
      created: Date.now(),
      lastAccess: Date.now(),
      totalReads: 0,
      totalWrites: 0
    }
  };

  // Track recent operations for collision detection
  const recentOperations = new Map<string | symbol, LedgerEntry[]>();

  // Helper to check for collisions
  function checkCollision(property: string | symbol, newEntry: LedgerEntry): void {
    const recent = recentOperations.get(property) || [];
    const now = newEntry.timestamp;
    
    // Filter recent operations within collision window
    const recentInWindow = recent.filter(entry => 
      now - entry.timestamp < collisionWindow && 
      entry.threadId !== newEntry.threadId
    );
    
    if (recentInWindow.length > 0) {
      const collision = {
        timestamp: now,
        property,
        operations: [...recentInWindow, newEntry]
      };
      ledger.collisions.push(collision);
    }
    
    // Update recent operations
    recentOperations.set(property, [
      ...recent.filter(entry => now - entry.timestamp < collisionWindow),
      newEntry
    ]);
  }

  // Helper to add entry to ledger
  function addEntry(entry: Omit<LedgerEntry, 'timestamp' | 'stackTrace' | 'threadId'>): void {
    const fullEntry: LedgerEntry = {
      ...entry,
      timestamp: Date.now(),
      stackTrace: enableStackTrace ? getStackTrace() : '',
      threadId
    };
    
    ledger.entries.push(fullEntry);
    ledger.metadata.lastAccess = fullEntry.timestamp;
    
    if (entry.operation === 'read') {
      ledger.metadata.totalReads++;
    } else {
      ledger.metadata.totalWrites++;
      // Add to pending reconciliation for write operations
      ledger.reconciliation.pending.push(fullEntry);
    }
    
    checkCollision(entry.property, fullEntry);
  }

  // Create the proxy
  const proxy = new Proxy(initial, {
    get(target, property, receiver) {
      // Handle access to the ledger symbol
      if (property === LEDGER_SYMBOL) {
        return ledger;
      }
      
      // Record read operation
      const value = Reflect.get(target, property, receiver);
      addEntry({
        operation: 'read',
        property,
        value
      });
      
      return value;
    },
    
    set(target, property, value, receiver) {
      // Record write operation
      const previousValue = Reflect.get(target, property, receiver);
      addEntry({
        operation: 'write',
        property,
        value,
        previousValue
      });
      
      return Reflect.set(target, property, value, receiver);
    },
    
    deleteProperty(target, property) {
      // Record delete operation as a write
      const previousValue = Reflect.get(target, property);
      addEntry({
        operation: 'write',
        property,
        value: undefined,
        previousValue
      });
      
      return Reflect.deleteProperty(target, property);
    },
    
    has(target, property) {
      // Record property check as read
      const hasProperty = Reflect.has(target, property);
      addEntry({
        operation: 'read',
        property: `has:${String(property)}`,
        value: hasProperty
      });
      
      return hasProperty;
    }
  });

  // Attach the ledger to the proxy
  (proxy as any)[LEDGER_SYMBOL] = ledger;
  
  return proxy as T & { [LEDGER_SYMBOL]: LedgerLog };
}

// Utility functions for working with ledger proxies
export function getLedger<T>(proxy: T & { [LEDGER_SYMBOL]: LedgerLog }): LedgerLog {
  return proxy[LEDGER_SYMBOL];
}

export function hasCollisions<T>(proxy: T & { [LEDGER_SYMBOL]: LedgerLog }): boolean {
  return proxy[LEDGER_SYMBOL].collisions.length > 0;
}

export function getCollisions<T>(proxy: T & { [LEDGER_SYMBOL]: LedgerLog }) {
  return proxy[LEDGER_SYMBOL].collisions;
}

export function getLedgerStats<T>(proxy: T & { [LEDGER_SYMBOL]: LedgerLog }) {
  const ledger = proxy[LEDGER_SYMBOL];
  return {
    totalOperations: ledger.entries.length,
    totalReads: ledger.metadata.totalReads,
    totalWrites: ledger.metadata.totalWrites,
    collisions: ledger.collisions.length,
    duration: ledger.metadata.lastAccess - ledger.metadata.created
  };
}

export function getLedgerEntries<T>(proxy: T & { [LEDGER_SYMBOL]: LedgerLog }) {
  return proxy[LEDGER_SYMBOL].entries;
}

export function clearLedger<T>(proxy: T & { [LEDGER_SYMBOL]: LedgerLog }) {
  const ledger = proxy[LEDGER_SYMBOL];
  ledger.entries = [];
  ledger.collisions = [];
  ledger.reconciliation.pending = [];
  ledger.reconciliation.reconciled = [];
  ledger.reconciliation.conflicts = [];
  ledger.metadata.totalReads = 0;
  ledger.metadata.totalWrites = 0;
  ledger.metadata.lastAccess = Date.now();
}

export function reconcileLedger<T>(proxy: T & { [LEDGER_SYMBOL]: LedgerLog }): {
  conflicts: Array<{
    property: string | symbol;
    operations: LedgerEntry[];
    conflictType: 'overwrite' | 'nested' | 'race';
  }>;
  reconciled: LedgerEntry[];
} {
  const ledger = proxy[LEDGER_SYMBOL];
  const pending = ledger.reconciliation.pending;
  const conflicts: Array<{
    property: string | symbol;
    operations: LedgerEntry[];
    conflictType: 'overwrite' | 'nested' | 'race';
  }> = [];
  
  // Group operations by property
  const propertyGroups = new Map<string | symbol, LedgerEntry[]>();
  
  for (const entry of pending) {
    const key = entry.property;
    if (!propertyGroups.has(key)) {
      propertyGroups.set(key, []);
    }
    propertyGroups.get(key)!.push(entry);
  }
  
  // Check for conflicts in each property group
  for (const [property, operations] of propertyGroups) {
    if (operations.length > 1) {
      // Sort by timestamp to see the sequence
      operations.sort((a, b) => a.timestamp - b.timestamp);
      
      // Check for overwrites (same property written multiple times)
      const writes = operations.filter(op => op.operation === 'write');
      if (writes.length > 1) {
        conflicts.push({
          property,
          operations: writes,
          conflictType: 'overwrite'
        });
      }
      
      // Check for race conditions (operations close in time)
      const timeWindow = 50; // 50ms window for race condition detection
      const raceGroups: LedgerEntry[][] = [];
      let currentGroup: LedgerEntry[] = [];
      
      for (let i = 0; i < operations.length; i++) {
        if (currentGroup.length === 0) {
          currentGroup = [operations[i]];
        } else {
          const lastOp = currentGroup[currentGroup.length - 1];
          if (operations[i].timestamp - lastOp.timestamp < timeWindow) {
            currentGroup.push(operations[i]);
          } else {
            if (currentGroup.length > 1) {
              raceGroups.push([...currentGroup]);
            }
            currentGroup = [operations[i]];
          }
        }
      }
      
      if (currentGroup.length > 1) {
        raceGroups.push(currentGroup);
      }
      
      // Add race condition conflicts
      for (const raceGroup of raceGroups) {
        conflicts.push({
          property,
          operations: raceGroup,
          conflictType: 'race'
        });
      }
    }
  }
  
  // Check for nested property conflicts
  const nestedConflicts = checkNestedConflicts(pending);
  conflicts.push(...nestedConflicts);
  
  // Move pending to reconciled
  ledger.reconciliation.reconciled.push(...pending);
  ledger.reconciliation.pending = [];
  ledger.reconciliation.conflicts.push(...conflicts);
  
  return {
    conflicts,
    reconciled: ledger.reconciliation.reconciled
  };
}

function checkNestedConflicts(operations: LedgerEntry[]): Array<{
  property: string | symbol;
  operations: LedgerEntry[];
  conflictType: 'overwrite' | 'nested' | 'race';
}> {
  const conflicts: Array<{
    property: string | symbol;
    operations: LedgerEntry[];
    conflictType: 'overwrite' | 'nested' | 'race';
  }> = [];
  
  // Group operations by their object paths
  const objectPaths = new Map<string, LedgerEntry[]>();
  
  for (const op of operations) {
    if (op.operation === 'write' && typeof op.value === 'object' && op.value !== null) {
      // This is a write to an object, check if any nested properties were modified
      const objectKey = String(op.property);
      if (!objectPaths.has(objectKey)) {
        objectPaths.set(objectKey, []);
      }
      objectPaths.get(objectKey)!.push(op);
    }
  }
  
  // Check for nested conflicts
  for (const [objectKey, ops] of objectPaths) {
    if (ops.length > 1) {
      // Multiple writes to the same object - potential nested conflicts
      conflicts.push({
        property: objectKey,
        operations: ops,
        conflictType: 'nested'
      });
    }
  }
  
  return conflicts;
}

export function printLedger<T>(proxy: T & { [LEDGER_SYMBOL]: LedgerLog }): void {
  const ledger = proxy[LEDGER_SYMBOL];
  if (!ledger) {
    console.log('Not a ledger item');
    return;
  }
  
  console.log('=== LEDGER REPORT ===');
  console.log(`Initial State:`, ledger.initial);
  console.log(`Total Operations: ${ledger.entries.length}`);
  console.log(`Reads: ${ledger.metadata.totalReads}, Writes: ${ledger.metadata.totalWrites}`);
  console.log(`Collisions: ${ledger.collisions.length}`);
  console.log(`Pending Reconciliation: ${ledger.reconciliation.pending.length}`);
  console.log(`Reconciled Operations: ${ledger.reconciliation.reconciled.length}`);
  console.log(`Reconciliation Conflicts: ${ledger.reconciliation.conflicts.length}`);
  
  if (ledger.collisions.length > 0) {
    console.log('\n=== COLLISIONS ===');
    ledger.collisions.forEach((collision, i) => {
      console.log(`Collision ${i + 1} on property "${String(collision.property)}":`);
      collision.operations.forEach(op => {
        console.log(`  ${op.operation.toUpperCase()} by ${op.threadId} at ${new Date(op.timestamp).toISOString()}`);
      });
    });
  }
  
  if (ledger.reconciliation.conflicts.length > 0) {
    console.log('\n=== RECONCILIATION CONFLICTS ===');
    ledger.reconciliation.conflicts.forEach((conflict, i) => {
      console.log(`Conflict ${i + 1} on property "${String(conflict.property)}" (${conflict.conflictType}):`);
      conflict.operations.forEach(op => {
        const time = new Date(op.timestamp).toISOString();
        const val = op.value !== undefined ? ` = ${safeStringify(op.value)}` : '';
        console.log(`  ${op.operation.toUpperCase()} at ${time}${val}`);
      });
    });
  }
  
  console.log('\n=== OPERATION LOG ===');
  ledger.entries.forEach((entry, i) => {
    const time = new Date(entry.timestamp).toISOString();
    const op = entry.operation.toUpperCase();
    const prop = String(entry.property);
    
    // Safe JSON stringify with circular reference handling
    let val = '';
    if (entry.value !== undefined) {
      val = ` = ${safeStringify(entry.value)}`;
    }
    
    console.log(`${i + 1}. [${time}] ${op} ${prop}${val}`);
  });
}
